import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hrApi, type Designation } from '@/api/hr';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface FormShape {
  name: string;
  departmentId?: number;
}

export function HrDesignationsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('HR_MGMT:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('HR_MGMT:EDIT'));

  const [editing, setEditing] = useState<Designation | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'designations', 'all'],
    queryFn: () => hrApi.listDesignations(false),
  });

  const { data: departments } = useQuery({
    queryKey: ['setup', 'lookups', 'departments', 'active'],
    queryFn: () => setupApi.listLookup('departments', true),
    staleTime: 5 * 60 * 1000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body = { name: values.name.trim(), departmentId: values.departmentId ?? null };
      return editing ? hrApi.updateDesignation(editing.id, body) : hrApi.createDesignation(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Designation updated' : 'Designation created');
      qc.invalidateQueries({ queryKey: ['hr', 'designations'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => hrApi.toggleDesignation(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'designations'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
  };
  const openEdit = (row: Designation) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({ name: row.name, departmentId: row.departmentId ?? undefined });
  };

  const open = creating || editing != null;

  const columns: TableProps<Designation>['columns'] = [
    {
      title: 'Designation',
      dataIndex: 'name',
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Department',
      dataIndex: 'departmentName',
      width: 220,
      render: (n: string | null) => (n ? <Tag>{n}</Tag> : <span style={{ opacity: 0.5 }}>—</span>),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Staff"
        title="Designations"
        description="Job titles. Optionally tag each one with a department so the staff form can cascade department → designation."
        count={{ value: data?.length ?? 0, label: 'designations' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New designation
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New designation'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(values) => upsert.mutate(values)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus placeholder="e.g. Subject Teacher" />
            </Form.Item>
            <Form.Item
              label="Department (optional)"
              name="departmentId"
              tooltip="Tag this designation with a department so it filters into that department's staff form."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(departments ?? []).map((d) => ({ value: d.id, label: d.name }))}
              />
            </Form.Item>
            <Space size="small">
              <span style={{ fontSize: 12, opacity: 0.6 }}>
                Tip: depts and employment types are managed under Setup → Lookup catalogs.
              </span>
            </Space>
          </Form>
        )}
      </Modal>
    </div>
  );
}
